import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { useAuth } from '../context/AuthContext';
import { useCart } from '../context/CartContext';
import { Disclosure, Transition } from '@headlessui/react';
import { 
    ChevronUpIcon, 
    ClipboardDocumentIcon, 
    ArrowUturnLeftIcon,
    ClockIcon,
    TruckIcon,
    CheckBadgeIcon,
    XCircleIcon,
    MapPinIcon,
    PhoneIcon,
    UserIcon,
    CalendarIcon,
    CreditCardIcon,
    ShoppingBagIcon,
    EyeIcon,
    StarIcon,
    HeartIcon,
    ShareIcon,
    PrinterIcon
} from '@heroicons/react/20/solid';
import { toast } from 'react-toastify';
import { useNavigate } from 'react-router-dom';
import api from '../utils/api';
import { getTrackingUrl, isKargonomiReference } from '../utils/shippingUtils';
import { openTrackingLink, isValidTrackingNumber, beautifyCompanyName } from '../utils/cargoTrackingUtils';
import Modal from '../components/Modal';
import CampaignRefundCalculation from '../components/CampaignRefundCalculation';
import ReviewForm from '../components/ReviewForm';


// Tailwind JIT motorunun sınıfları tanıması için tam sınıf adları kullanılır.
const statusConfig = {
    pending: { text: 'Hazırlanıyor', icon: ClockIcon, badgeStyle: 'bg-yellow-100 text-yellow-800' },
    shipped: { text: 'Kargoladı', icon: TruckIcon, badgeStyle: 'bg-brand-green bg-opacity-20 text-brand-green' },
    delivered: { text: 'Teslim Edildi', icon: CheckBadgeIcon, badgeStyle: 'bg-green-100 text-green-800' },
    cancelled: { text: 'İptal Edildi', icon: XCircleIcon, badgeStyle: 'bg-red-100 text-red-800' },
    return_requested: { text: 'İade Talebi İnceleniyor', icon: ArrowUturnLeftIcon, badgeStyle: 'bg-orange-100 text-orange-800' },
    return_approved: { text: 'İade Onaylanı', icon: CheckBadgeIcon, badgeStyle: 'bg-teal-100 text-teal-800' },
    return_rejected: { text: 'İade Reddedildi', icon: XCircleIcon, badgeStyle: 'bg-red-100 text-red-800' },
    return_shipped: { text: 'İade Kargoya Verildi', icon: TruckIcon, badgeStyle: 'bg-purple-100 text-purple-800' },
    return_received: { text: 'İade Kargo Alındı', icon: CheckBadgeIcon, badgeStyle: 'bg-indigo-100 text-indigo-800' },
    return_completed: { text: 'İade Tamamlandı', icon: CheckBadgeIcon, badgeStyle: 'bg-green-100 text-green-800' },
};

const paymentStatusConfig = {
    pending: { text: 'Ödeme Bekliyor', badgeStyle: 'bg-yellow-100 text-yellow-800', icon: '⌛' },
    paid: { text: 'Ödendi', badgeStyle: 'bg-green-100 text-green-800', icon: '✅' },
    failed: { text: 'Ödeme Başarısız', badgeStyle: 'bg-red-100 text-red-800', icon: '❌' },
    refunded: { text: 'İade Edildi', badgeStyle: 'bg-purple-100 text-purple-800', icon: '🔄' },
    partial_refund: { text: 'Kısmi İade', badgeStyle: 'bg-orange-100 text-orange-800', icon: '⚠️' },
};



// Ödeme Bilgileri Komponenti
// Component removed

// Main OrdersPage Component
function OrdersPage({ embedded = false, openModal: openModalProp, closeModal: closeModalProp, onInitiateReturn }) {
    const [orders, setOrders] = useState([]);
    const [loading, setLoading] = useState(true);
    const { token } = useAuth();
    const { addToCart } = useCart();
    const navigate = useNavigate();
    const [isInternalModalOpen, setIsInternalModalOpen] = useState(false);
    const [selectedOrder, setSelectedOrder] = useState(null);
    const [selectedOrderDetails, setSelectedOrderDetails] = useState(null); // For details modal
    const [expandedOrders, setExpandedOrders] = useState(new Set());
    const [modalContent, setModalContent] = useState(null);
    const [reviewFormData, setReviewFormData] = useState(null); // { orderId, productId, productName }
    const [searchTerm, setSearchTerm] = useState('');
    const [activeFilter, setActiveFilter] = useState('all'); // all, pending, shipped, delivered, cancelled
    const [isSearchExpanded, setIsSearchExpanded] = useState(false);

    const fetchOrders = useCallback(async () => {
        if (!token) { setLoading(false); return; }
        setLoading(true);
        try {
            const data = await api.get('/api/orders', { headers: { Authorization: `Bearer ${token}` } });
            setOrders(data);
        } catch (err) {
            toast.error("Siparişler getirilirken bir hata oluştu.");
        } finally {
            setLoading(false);
        }
    }, [token]);

    useEffect(() => { 
        fetchOrders();
    }, [fetchOrders]);

    // Shipping address'i parse et
    const parseShippingAddress = (order) => {
        if (!order.shipping_address) return null;
        
        if (typeof order.shipping_address === 'string') {
            try {
                return JSON.parse(order.shipping_address);
            } catch (e) {
                console.error('Shipping address parse hatası:', e);
                return null;
            }
        }
        
        return order.shipping_address;
    };

    // Selected shipping'i parse et
    const parseSelectedShipping = (order) => {
        if (!order.selected_shipping) return null;
        
        if (typeof order.selected_shipping === 'string') {
            try {
                return JSON.parse(order.selected_shipping);
            } catch (e) {
                console.error('Selected shipping parse hatası:', e);
                return null;
            }
        }
        
        return order.selected_shipping;
    };


    const handleModifyOrder = async (order) => {
        if (!window.confirm("Siparişi düzenlemek için mevcut siparişiniz iptal edilecek ve ürünler sepetinize eklenecektir. Devam etmek istiyor musunuz?")) return;
        try {
            await api.post(`/api/orders/${order.id}/cancel`, {}, { headers: { 'Authorization': `Bearer ${token}` } });
            toast.info("Siparişiniz iptal edildi, ürünleriniz düzenleme için sepetinize ekleniyor...");
            for (const item of order.order_items) {
                addToCart(item.products, item.product_variants, item.quantity);
            }
            toast.success("Ürünleriniz sepetinize eklendi.");
            navigate('/cart');
        } catch (err) {
            toast.error(err.response?.data?.error || "Sipariş düzenlenirken bir hata oluştu.");
            fetchOrders();
        }
    };

    const handleCloseModal = () => {
        setIsInternalModalOpen(false);
        setSelectedOrder(null);
    };

    const handleOpenReturnModal = (order) => {
        if (onInitiateReturn) {
            onInitiateReturn(order);
        } else {
            toast.warn('İade işlemi sadece Hesabım sayfası üzerinden yapılabilir.');
        }
    };

    const isReturnable = (order) => {
        // Sipariş kargolanmış, teslim edilmiş veya tamamlanmış olmalı
        if (!['shipped', 'delivered', 'completed'].includes(order.status)) return false;
        
        // Zaten iade talebi varsa false
        if (order.returns && order.returns.length > 0) return false;
        
        // 15 günlük iade süresi kontrolü
        if (order.return_eligible_until) {
            const now = new Date();
            const returnDeadline = new Date(order.return_eligible_until);
            if (now > returnDeadline) return false;
        } else {
            // Eğer return_eligible_until yoksa, teslim tarihinden 15 gün sonrasını hesapla
            const deliveredDate = new Date(order.updated_at);
            const returnDeadline = new Date(deliveredDate.getTime() + (15 * 24 * 60 * 60 * 1000)); // 15 gün
            const now = new Date();
            if (now > returnDeadline) return false;
        }
        
        return true;
    };

    const hasActiveReturn = (order) => {
        return order.returns && order.returns.length > 0;
    };

    const getReturnInfo = (order) => {
        if (!order.returns || order.returns.length === 0) return null;
        return order.returns[0]; // En son iade talebi
    };

    const isReturnExpired = (order) => {
        if (order.status !== 'delivered') return false;
        if (order.returns && order.returns.length > 0) return false;
        
        if (order.return_eligible_until) {
            const now = new Date();
            const returnDeadline = new Date(order.return_eligible_until);
            return now > returnDeadline;
        } else {
            const deliveredDate = new Date(order.updated_at);
            const returnDeadline = new Date(deliveredDate.getTime() + (15 * 24 * 60 * 60 * 1000));
            const now = new Date();
            return now > returnDeadline;
        }
    };

    const toggleOrderExpansion = (orderId) => {
        setExpandedOrders(prev => {
            const newSet = new Set(prev);
            if (newSet.has(orderId)) {
                newSet.delete(orderId);
            } else {
                newSet.add(orderId);
            }
            return newSet;
        });
    };

    const isOrderExpanded = (orderId) => {
        return expandedOrders.has(orderId);
    };

    const canCancelOrder = (order) => {
        return order.status === 'pending';
    };

    const canModifyOrder = (order) => {
        return order.status === 'pending';
    };

    const filteredOrders = useMemo(() => {
        let result = orders;
        
        // Status filtresi
        if (activeFilter !== 'all') {
            result = result.filter(order => order.status === activeFilter);
        }
        
        // Arama filtresi
        if (searchTerm.trim()) {
            const searchLower = searchTerm.toLowerCase();
            result = result.filter(order => {
                const shippingAddress = parseShippingAddress(order);
                return order.order_number?.toLowerCase().includes(searchLower) ||
                    shippingAddress?.full_name?.toLowerCase().includes(searchLower) ||
                    order.order_items?.some(item => 
                        item.product_name_at_purchase?.toLowerCase().includes(searchLower) ||
                        item.products?.name?.toLowerCase().includes(searchLower)
                    );
            });
        }
        
        return result;
    }, [orders, activeFilter, searchTerm]);

    const handleFilterClick = (filterId) => {
        setActiveFilter(filterId);
    };

    const handleShare = async (order) => {
        const shareText = `Minela Kids Sipariş #${order.order_number} - ₺${parseFloat(order.total_price).toFixed(2)}`;
        
        try {
            if (navigator.share && navigator.canShare) {
                await navigator.share({
                    title: 'Minela Kids Siparişim',
                    text: shareText,
                    url: window.location.href
                });
            } else {
                await navigator.clipboard.writeText(shareText);
                toast.success('Sipariş bilgisi kopyalandı!');
            }
        } catch (error) {
            console.error('Paylaşım hatası:', error);
            // Fallback: manuel kopyalama
            const textArea = document.createElement('textarea');
            textArea.value = shareText;
            document.body.appendChild(textArea);
            textArea.select();
            document.execCommand('copy');
            document.body.removeChild(textArea);
            toast.success('Sipariş bilgisi kopyalandı!');
        }
    };
    
    const handleCancelOrder = async (orderId, orderNumber) => {
        if (!window.confirm(`${orderNumber} siparişini iptal etmek istediğinizden emin misiniz? Ödeme yapıldıysa iade işlemi başlatılacaktır.`)) {
            return;
        }
        
        try {
            const response = await api.post('/api/payment/cancel-order', {
                orderId: orderId,
                reason: 'Müşteri talebi'
            }, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            
            if (response.success) {
                toast.success(response.message || 'Sipariş iptal edildi');
                // Siparişleri yeniden yükle
                fetchOrders();
            } else {
                throw new Error(response.error || 'Sipariş iptal edilemedi');
            }
        } catch (error) {
            console.error('İptal hatası:', error);
            toast.error(error.message || 'Sipariş iptal edilirken bir hata oluştu');
        }
    };

    if (loading) return (
        <div className="flex justify-center items-center h-64">
            <div className="relative">
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-gray-200"></div>
                <div className="animate-spin rounded-full h-16 w-16 border-4 border-brand-green border-t-transparent absolute top-0 left-0"></div>
            </div>
        </div>
    );

    const ordersContent = (
        <>
            {orders.length === 0 ? (
                <div className="text-center py-16">
                    <div className="w-24 h-24 bg-gray-100 rounded-full flex items-center justify-center mx-auto mb-4">
                        <svg className="w-12 h-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 11V7a4 4 0 00-8 0v4M8 11v6h8v-6M8 11H6a2 2 0 00-2 2v6a2 2 0 002 2h12a2 2 0 002-2v-6a2 2 0 00-2-2h-2" />
                        </svg>
                    </div>
                    <h3 className="text-xl font-semibold text-gray-600 mb-2">Sipariş Bulunamadı</h3>
                    <p className="text-gray-500">Henüz siparişiniz bulunmuyor.</p>
                </div>
            ) : (
                filteredOrders.map((order, index) => {
                    const statusInfo = statusConfig[order.status] || { text: order.status, icon: ClockIcon, badgeStyle: 'bg-gray-100 text-gray-800' };
                    const isExpanded = isOrderExpanded(order.id);
                    const shippingAddress = parseShippingAddress(order);
                    const selectedShipping = parseSelectedShipping(order);
                    
                    // Ürün görsellerini al (ilk 3 ürün)
                    const productImages = order.order_items?.slice(0, 3).map(item => 
                        item.products?.main_image_url || 'https://placehold.co/60x60/e0e0e0/ffffff?text=Ürün'
                    ) || [];
                    
                    return (
                        <div 
                            key={order.id} 
                            className="group bg-white rounded-2xl shadow-lg border border-gray-200 overflow-hidden hover:shadow-xl transition-all duration-300 mb-4 sm:mb-6 mx-1 sm:mx-2"
                        >
                            {/* Premium Optimized Sipariş Kartı */}
                            <div 
                                className="relative p-4 sm:p-6 lg:p-8 cursor-pointer hover:bg-gray-50/50 transition-colors duration-200"
                                onClick={() => setSelectedOrderDetails(selectedOrderDetails?.id === order.id ? null : order)}
                            >
                                {/* Ferah Layout */}
                                <div className="space-y-4 sm:space-y-5">
                                    {/* Üst Satır: Ürün Görseli + Sipariş Bilgileri */}
                                    <div className="flex items-start space-x-4 sm:space-x-6 lg:space-x-8">
                                        {/* Ürün Görseli - Optimize Edilmiş Boyutlar */}
                                        <div className="relative flex-shrink-0">
                                            <div className="w-16 h-16 sm:w-20 sm:h-20 lg:w-24 lg:h-24 rounded-xl overflow-hidden border-2 border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
                                                <img 
                                                    src={productImages[0] || 'https://placehold.co/96x96/e0e0e0/ffffff?text=Ürün'} 
                                                    alt="Ürün"
                                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                                />
                                            </div>
                                            {/* Ürün Sayısı Badge - Büyütülmüş */}
                                            <div className="absolute -top-2 -right-2 w-6 h-6 sm:w-7 sm:h-7 bg-brand-green rounded-full flex items-center justify-center shadow-xl border-2 border-white">
                                                <span className="text-white text-xs sm:text-sm font-bold">{order.order_items?.length || 0}</span>
                                            </div>
                                        </div>
                                        
                                        {/* Sipariş Bilgileri - Optimize Edilmiş Yazı Boyutları */}
                                        <div className="flex-1 min-w-0 space-y-3">
                                            {/* Sipariş No */}
                                            <div className="flex items-center justify-between">
                                                <h3 className="text-base sm:text-lg lg:text-xl font-bold text-gray-800 truncate">
                                                    #{order.order_number}
                                                </h3>
                                                <span className="text-lg sm:text-xl lg:text-2xl font-bold text-brand-green flex-shrink-0">
                                                    ₺{parseFloat(order.total_price).toFixed(2)}
                                                </span>
                                            </div>
                                            
                                            {/* Durum Badge - Büyütülmüş */}
                                            <div className="flex flex-wrap items-center gap-2">
                                                <div className={`inline-flex items-center px-3 py-2 rounded-xl text-sm sm:text-base font-semibold ${statusInfo.badgeStyle} shadow-md`}>
                                                    <statusInfo.icon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                                                    <span className="truncate">{statusInfo.text}</span>
                                                </div>
                                                {/* Payment Status Badge */}
                                                {order.payment_status && paymentStatusConfig[order.payment_status] && (
                                                    <div className={`inline-flex items-center px-3 py-1.5 rounded-lg text-xs sm:text-sm font-semibold ${paymentStatusConfig[order.payment_status].badgeStyle} shadow-sm`}>
                                                        <span className="mr-1">{paymentStatusConfig[order.payment_status].icon}</span>
                                                        {paymentStatusConfig[order.payment_status].text}
                                                    </div>
                                                )}
                                            </div>
                                            
                                            {/* Tarih - Büyütülmüş */}
                                            <div className="text-sm sm:text-base text-gray-600 font-medium">
                                                📅 {new Date(order.created_at).toLocaleDateString('tr-TR')}
                                            </div>
                                        </div>
                                    </div>

                                    {/* Alt Satır: Hızlı Eylemler - Büyütülmüş */}
                                    <div className="flex items-center justify-between pt-3 border-t border-gray-200">
                                        <div className="flex items-center space-x-4 text-sm sm:text-base text-gray-600 flex-1 min-w-0">
                                            <span className="flex items-center flex-shrink-0 font-medium">
                                                <ShoppingBagIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 text-brand-green" />
                                                {order.order_items?.length || 0} ürün
                                            </span>
                                            {order.tracking_number && (
                                                <span className="flex items-center text-blue-600 font-medium">
                                                    <TruckIcon className="w-4 h-4 sm:w-5 sm:h-5 mr-2 flex-shrink-0" />
                                                    <span className="truncate">Takip: #{order.tracking_number.slice(-6)}</span>
                                                </span>
                                            )}
                                        </div>
                                        
                                        {/* Sağ Taraf - Büyütülmüş Butonlar */}
                                        <div className="flex items-center space-x-2 flex-shrink-0">
                                            {/* İptal Butonu - Sadece pending ve processing için */}
                                            {['pending', 'processing'].includes(order.status) && order.payment_status === 'paid' && (
                                                <button
                                                    onClick={(e) => {
                                                        e.stopPropagation();
                                                        handleCancelOrder(order.id, order.order_number);
                                                    }}
                                                    className="p-2 sm:p-3 rounded-xl bg-red-100 hover:bg-red-600 text-red-600 hover:text-white transition-all duration-200 hover:scale-105 shadow-md"
                                                    title="Siparişi İptal Et"
                                                >
                                                    <XCircleIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                                </button>
                                            )}
                                            
                                            {/* Paylaş Butonu */}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    handleShare(order);
                                                }}
                                                className="p-2 sm:p-3 rounded-xl bg-gray-100 hover:bg-brand-green hover:text-white transition-all duration-200 hover:scale-105 shadow-md"
                                                title="Paylaş"
                                            >
                                                <ShareIcon className="w-4 h-4 sm:w-5 sm:h-5" />
                                            </button>
                                            
                                            {/* Expand Arrow */}
                                            <button
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setSelectedOrderDetails(selectedOrderDetails?.id === order.id ? null : order);
                                                }}
                                                className="p-2 sm:p-3 rounded-xl bg-gray-100 hover:bg-brand-green hover:text-white transition-all duration-200 hover:scale-105 shadow-md"
                                                title="Detayları Göster"
                                            >
                                                <svg 
                                                    className="w-4 h-4 sm:w-5 sm:h-5 transition-transform duration-200" 
                                                    fill="none" 
                                                    stroke="currentColor" 
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
                                                </svg>
                                            </button>
                                        </div>
                                    </div>
                                </div>
                            </div>

                            {/* Genişletilmiş İçerik - Inline (Akordiyon gibi) */}
                            {selectedOrderDetails?.id === order.id && (
                                <div className="mt-4 border-t border-gray-100 pt-4 bg-gray-50/50 rounded-b-2xl p-4 sm:p-6">
                                    <div className="flex justify-between items-center border-b pb-3 mb-4">
                                        <h3 className="text-xl font-bold text-gray-800">Sipariş Detayı #{order.order_number}</h3>
                                    </div>
                                    <div className="p-1">
                                        {/* Teslimat Adresi Bölümü - Mobil Optimized */}
                                        <div className="mb-4 p-4 bg-white rounded-xl border border-gray-200 shadow-sm">
                                            <h4 className="font-semibold text-gray-800 mb-4 flex items-center text-base">
                                                <div className="w-8 h-8 bg-brand-green rounded-lg flex items-center justify-center mr-3">
                                                    <MapPinIcon className="w-4 h-4 text-white" />
                                                </div>
                                                Teslimat Bilgileri
                                            </h4>
                                            
                                            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                <div className="space-y-3">
                                                    <div>
                                                        <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Alıcı</p>
                                                        <p className="font-semibold text-gray-800 text-sm">{shippingAddress?.full_name || 'Bilgi Yok'}</p>
                                                    </div>
                                                    
                                                    <div>
                                                        <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Telefon</p>
                                                        <p className="font-semibold text-gray-800 text-sm">{shippingAddress?.phone || 'Bilgi Yok'}</p>
                                                    </div>
                                                </div>
                                                
                                                <div className="space-y-3">
                                                    <div>
                                                        <p className="text-xs font-medium text-gray-500 mb-1 uppercase tracking-wide">Adres</p>
                                                        <p className="font-medium text-gray-800 leading-relaxed text-sm">
                                                            {shippingAddress?.address_line || 'Adres bilgisi mevcut değil'}
                                                        </p>
                                                        <p className="text-xs text-gray-600 mt-1">
                                                            {shippingAddress?.district}, {shippingAddress?.city}
                                                        </p>
                                                    </div>
                                                </div>
                                            </div>
                                            
                                            {/* Kargo ve Takip Bilgileri - Detaylı Versiyon */}
                                            {(order.shipping_company || order.tracking_number) && (
                                                <div className="mt-4 pt-4 border-t border-gray-100">
                                                    <h5 className="text-sm font-semibold text-gray-700 mb-3 flex items-center">
                                                        <TruckIcon className="w-4 h-4 mr-2 text-brand-green" />
                                                        Kargo Bilgileri
                                                    </h5>
                                                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                                                        {order.shipping_company && (
                                                            <div className="bg-blue-50 rounded-lg p-3">
                                                                <p className="text-xs font-medium text-blue-600 mb-1 uppercase tracking-wide">Kargo Firması</p>
                                                                <p className="font-semibold text-blue-800 text-sm">{order.shipping_company}</p>
                                                            </div>
                                                        )}
                                                        
                                                        {order.tracking_number && (
                                                            <div className="bg-green-50 rounded-lg p-3">
                                                                <p className="text-xs font-medium text-green-600 mb-1 uppercase tracking-wide">
                                                                    {isKargonomiReference(order.tracking_number) ? 'Referans Numarası' : 'Takip Numarası'}
                                                                </p>
                                                                <div className="flex items-center justify-between">
                                                                    <p className="font-mono text-sm text-green-800 font-bold">{order.tracking_number}</p>
                                                                    {!isKargonomiReference(order.tracking_number) && (
                                                                        <button
                                                                            onClick={(e) => {
                                                                                e.stopPropagation();
                                                                                if (isValidTrackingNumber(order.tracking_number)) {
                                                                                    const success = openTrackingLink(order.shipping_company, order.tracking_number);
                                                                                    if (success) {
                                                                                        toast.success(`${beautifyCompanyName(order.shipping_company)} takip sayfası açılıyor...`);
                                                                                    } else {
                                                                                        toast.warn(`${beautifyCompanyName(order.shipping_company)} için takip linki bulunamadı.`);
                                                                                    }
                                                                                } else {
                                                                                    toast.warn('Geçersiz takip numarası');
                                                                                }
                                                                            }}
                                                                            className="text-green-600 hover:text-green-800 hover:bg-green-100 px-2 py-1 rounded transition-all duration-200"
                                                                            title="Takip sayfasını aç"
                                                                        >
                                                                            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                                                                            </svg>
                                                                        </button>
                                                                    )}
                                                                </div>
                                                                {isKargonomiReference(order.tracking_number) && (
                                                                    <div className="mt-2 text-center w-full">
                                                                        <span className="inline-block text-xs text-yellow-600 font-medium bg-yellow-50 px-2 py-1 rounded-md border border-yellow-100 animate-pulse break-words max-w-full">
                                                                            Gerçek takip no bekleniyor...
                                                                        </span>
                                                                    </div>
                                                                )}
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                            )}
                                        </div>

                                        {/* Ürünler Bölümü - Mobil Optimized */}
                                        <div className="mb-4">
                                            <h4 className="font-semibold text-gray-800 mb-4 flex items-center text-base">
                                                <div className="w-8 h-8 bg-brand-green rounded-lg flex items-center justify-center mr-3">
                                                    <ShoppingBagIcon className="w-4 h-4 text-white" />
                                                </div>
                                                Sipariş İçeriği ({order.order_items?.length || 0} ürün)
                                            </h4>
                                            
                                            <div className="space-y-3">
                                                {order.order_items?.map((item, itemIndex) => (
                                                    <div 
                                                        key={itemIndex} 
                                                        className="flex items-center space-x-4 sm:space-x-6 lg:space-x-8 p-4 sm:p-6 lg:p-8 bg-white rounded-xl border border-gray-200 shadow-lg hover:shadow-xl transition-all duration-300 hover:scale-102"
                                                    >
                                                        {/* Ürün Görseli - Premium Büyütülmüş */}
                                                        <div className="relative flex-shrink-0">
                                                            <div className="w-20 h-20 sm:w-24 sm:h-24 lg:w-28 lg:h-28 rounded-xl overflow-hidden border-2 border-gray-200 shadow-lg hover:shadow-xl transition-shadow duration-300">
                                                                <img 
                                                                    src={item.products?.main_image_url || 'https://placehold.co/112x112/e0e0e0/ffffff?text=Ürün'} 
                                                                    alt={item.product_name_at_purchase}
                                                                    className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                                                                />
                                                            </div>
                                                            
                                                            {/* Quantity Badge - Büyütülmüş */}
                                                            <div className="absolute -top-2 -right-2 w-7 h-7 sm:w-8 sm:h-8 bg-brand-green rounded-full flex items-center justify-center text-white font-bold text-sm shadow-xl border-2 border-white">
                                                                {item.quantity}
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Ürün Bilgileri - Büyütülmüş Yazılar */}
                                                        <div className="flex-1 min-w-0">
                                                            <h5 className="font-bold text-gray-800 text-base sm:text-lg lg:text-xl mb-3 line-clamp-2">
                                                                {item.product_name_at_purchase || item.products?.name}
                                                            </h5>
                                                            <div className="flex flex-wrap items-center gap-2 text-sm sm:text-base text-gray-600 mb-3">
                                                                <span className="bg-gray-100 px-3 py-2 rounded-lg font-medium">
                                                                    {item.variant_details_at_purchase?.color || item.product_variants?.color}
                                                                </span>
                                                                <span className="bg-gray-100 px-3 py-2 rounded-lg font-medium">
                                                                    {item.variant_details_at_purchase?.size || item.product_variants?.size}
                                                                </span>
                                                            </div>
                                                            <div className="flex items-center justify-between mb-3">
                                                                <span className="text-sm sm:text-base text-gray-600 font-medium">
                                                                    {item.quantity} × ₺{parseFloat(item.price_at_purchase).toFixed(2)}
                                                                </span>
                                                                <span className="font-bold text-brand-green text-lg sm:text-xl lg:text-2xl">
                                                                    ₺{(parseFloat(item.price_at_purchase) * item.quantity).toFixed(2)}
                                                                </span>
                                                            </div>
                                                            
                                                            {/* Yorum Yaz Butonu - Sadece Teslim Edilen Siparişler İçin */}
                                                            {order.status === 'delivered' && (
                                                                <button
                                                                    onClick={(e) => {
                                                                        e.stopPropagation();
                                                                        setReviewFormData({
                                                                            orderId: order.id,
                                                                            productId: item.product_id,
                                                                            productName: item.product_name_at_purchase || item.products?.name
                                                                        });
                                                                    }}
                                                                    className="w-full px-4 py-2 bg-brand-green text-white rounded-lg hover:bg-green-600 transition-all duration-200 font-medium flex items-center justify-center gap-2 shadow-md hover:shadow-lg"
                                                                >
                                                                    <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z" />
                                                                    </svg>
                                                                    Yorum Yaz
                                                                </button>
                                                            )}
                                                        </div>
                                                    </div>
                                                ))}
                                            </div>
                                        </div>

                                        {/* Kampanya Bilgileri Bölümü */}
                                        {(order.coupon_discount > 0 || order.category_discount > 0 || order.special_day_discount > 0 || order.cart_discount > 0 || order.flash_sale_discount > 0 || order.package_discount > 0) && (
                                            <div className="mb-4">
                                                <h4 className="font-semibold text-gray-800 mb-4 flex items-center text-base">
                                                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center mr-3">
                                                        <span className="text-white text-sm font-bold">🎉</span>
                                                    </div>
                                                    Uygulanan Kampanyalar
                                                </h4>
                                                
                                                <div className="space-y-3">
                                                    {/* Kupon Kampanyası */}
                                                    {order.coupon_discount > 0 && (
                                                        <div className="bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-300 p-4 rounded-xl">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-3">
                                                                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                                                                        <span className="text-white text-sm font-bold">🎫</span>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-green-800 font-bold text-sm">Kupon İndirimi</p>
                                                                        <p className="text-green-600 text-xs">{order.coupon_code}</p>
                                                                    </div>
                                                                </div>
                                                                <span className="text-green-700 font-bold">-₺{order.coupon_discount.toFixed(2)}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Kategori Kampanyası */}
                                                    {order.category_discount > 0 && (
                                                        <div className="bg-gradient-to-r from-blue-100 to-indigo-100 border-2 border-blue-300 p-4 rounded-xl">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-3">
                                                                    <div className="w-8 h-8 bg-blue-500 rounded-lg flex items-center justify-center">
                                                                        <span className="text-white text-sm font-bold">🏷️</span>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-blue-800 font-bold text-sm">Kategori İndirimi</p>
                                                                        <p className="text-blue-600 text-xs">Kategori kampanyası uygulandı</p>
                                                                    </div>
                                                                </div>
                                                                <span className="text-blue-700 font-bold">-₺{order.category_discount.toFixed(2)}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Özel Gün Kampanyası */}
                                                    {order.special_day_discount > 0 && (
                                                        <div className="bg-gradient-to-r from-yellow-100 to-orange-100 border-2 border-yellow-300 p-4 rounded-xl">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-3">
                                                                    <div className="w-8 h-8 bg-yellow-500 rounded-lg flex items-center justify-center">
                                                                        <span className="text-white text-sm font-bold">🎊</span>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-yellow-800 font-bold text-sm">Özel Gün İndirimi</p>
                                                                        <p className="text-yellow-600 text-xs">Özel gün kampanyası uygulandı</p>
                                                                    </div>
                                                                </div>
                                                                <span className="text-yellow-700 font-bold">-₺{order.special_day_discount.toFixed(2)}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Sepet Kampanyası */}
                                                    {order.cart_discount > 0 && (
                                                        <div className="bg-gradient-to-r from-green-100 to-emerald-100 border-2 border-green-300 p-4 rounded-xl">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-3">
                                                                    <div className="w-8 h-8 bg-green-500 rounded-lg flex items-center justify-center">
                                                                        <span className="text-white text-sm font-bold">🛒</span>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-green-800 font-bold text-sm">Sepet İndirimi</p>
                                                                        <p className="text-green-600 text-xs">Sepet kampanyası uygulandı</p>
                                                                    </div>
                                                                </div>
                                                                <span className="text-green-700 font-bold">-₺{order.cart_discount.toFixed(2)}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Flash Sale Kampanyası */}
                                                    {order.flash_sale_discount > 0 && (
                                                        <div className="bg-gradient-to-r from-red-100 to-orange-100 border-2 border-red-300 p-4 rounded-xl">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-3">
                                                                    <div className="w-8 h-8 bg-red-500 rounded-lg flex items-center justify-center animate-pulse">
                                                                        <span className="text-white text-sm font-bold">⚡</span>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-red-800 font-bold text-sm">Flash Sale İndirimi</p>
                                                                        <p className="text-red-600 text-xs">Flash sale kampanyası uygulandı</p>
                                                                    </div>
                                                                </div>
                                                                <span className="text-red-700 font-bold">-₺{order.flash_sale_discount.toFixed(2)}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                    
                                                    {/* Paket Kampanyası */}
                                                    {order.package_discount > 0 && (
                                                        <div className="bg-gradient-to-r from-purple-100 to-indigo-100 border-2 border-purple-300 p-4 rounded-xl">
                                                            <div className="flex items-center justify-between">
                                                                <div className="flex items-center space-x-3">
                                                                    <div className="w-8 h-8 bg-purple-500 rounded-lg flex items-center justify-center">
                                                                        <span className="text-white text-sm font-bold">📦</span>
                                                                    </div>
                                                                    <div>
                                                                        <p className="text-purple-800 font-bold text-sm">Paket Kampanyası</p>
                                                                        <p className="text-purple-600 text-xs">2 Al 1 Öde kampanyası uygulandı</p>
                                                                    </div>
                                                                </div>
                                                                <span className="text-purple-700 font-bold">-₺{order.package_discount.toFixed(2)}</span>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            </div>
                                        )}

                                        {/* İade Bilgileri artık İadelerim sekmesinde */}

                                        {/* İade Süresi Geçmiş Uyarısı - Çekmece İçinde */}
                                        {isReturnExpired(order) && (
                                            <div className="mt-4">
                                                <div className="bg-red-50 border border-red-200 rounded-lg p-4">
                                                    <div className="flex items-center">
                                                        <XCircleIcon className="w-5 h-5 text-red-500 mr-2" />
                                                        <span className="text-red-700 text-sm font-medium">
                                                            İade süresi dolmuş. Bu sipariş için artık iade talebi oluşturamazsınız.
                                                        </span>
                                                    </div>
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                </div>
                            )}

                            {/* Eylem Butonları - Premium Optimized */}
                            <div className="px-4 sm:px-6 lg:px-8 pb-4 sm:pb-6">
                                <div className="flex flex-col sm:flex-row gap-3 sm:gap-4">
                                    {canModifyOrder(order) && (
                                        <button 
                                            onClick={() => handleModifyOrder(order)} 
                                            className="flex-1 bg-gradient-to-r from-brand-green to-brand-green-light hover:from-brand-green-light hover:to-brand-green text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-xl transition-all duration-200 flex items-center justify-center text-sm sm:text-base lg:text-lg shadow-lg hover:shadow-xl hover:scale-105"
                                        >
                                            <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 mr-2 sm:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                                            </svg>
                                            <span className="hidden sm:inline">Siparişi </span>Düzenle
                                        </button>
                                    )}
                                    {canCancelOrder(order) && (
                                        <button 
                                            onClick={() => handleCancelOrder(order.id)} 
                                            className="flex-1 bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-xl transition-all duration-200 flex items-center justify-center text-sm sm:text-base lg:text-lg shadow-lg hover:shadow-xl hover:scale-105"
                                        >
                                            <svg className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 mr-2 sm:mr-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                            Siparişi İptal Et
                                        </button>
                                    )}
                                    {isReturnable(order) && (
                                        <button 
                                            onClick={() => handleOpenReturnModal(order)} 
                                            className="flex-1 bg-gradient-to-r from-orange-500 to-orange-600 hover:from-orange-600 hover:to-orange-700 text-white font-semibold py-3 sm:py-4 px-4 sm:px-6 rounded-xl transition-all duration-200 flex items-center justify-center text-sm sm:text-base lg:text-lg shadow-lg hover:shadow-xl hover:scale-105"
                                        >
                                            <ArrowUturnLeftIcon className="w-4 h-4 sm:w-5 sm:h-5 lg:w-6 lg:h-6 mr-2 sm:mr-3" />
                                            <div className="flex flex-col items-center">
                                                <span>İade Talebi Oluştur</span>
                                                {order.return_eligible_until && (
                                                    <span className="text-xs opacity-90">
                                                        {(() => {
                                                            const now = new Date();
                                                            const deadline = new Date(order.return_eligible_until);
                                                            const diffTime = deadline - now;
                                                            const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
                                                            return diffDays > 0 ? `${diffDays} gün kaldı` : 'Son gün';
                                                        })()}
                                                    </span>
                                                )}
                                            </div>
                                        </button>
                                    )}
                                    </div>
                                </div>
                        </div>
                    );
                })
            )}
        </>
    );

    if (embedded) {
        const tabs = [
            { id: 'all', label: 'Tüm Siparişler' },
            { id: 'pending', label: 'Hazırlanıyor' },
            { id: 'shipped', label: 'Kargolandı' },
            { id: 'delivered', label: 'Teslim Edildi' },
            { id: 'cancelled', label: 'İptal Edildi' }
        ];

        return (
            <div className="space-y-6 max-w-5xl mx-auto">
                <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-4">
                    <div className="flex flex-col sm:flex-row justify-between items-center space-y-4 sm:space-y-0">
                        {/* Tabs */}
                        <div className="flex overflow-x-auto w-full sm:w-auto space-x-1 sm:space-x-2 pb-2 sm:pb-0 scrollbar-hide" style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}>
                            {tabs.map(tab => (
                                <button
                                    key={tab.id}
                                    onClick={() => setActiveFilter(tab.id)}
                                    className={`whitespace-nowrap px-4 py-2 text-sm font-medium rounded-lg transition-colors duration-200 ${
                                        activeFilter === tab.id
                                            ? 'bg-brand-green text-white shadow-md'
                                            : 'text-gray-600 hover:bg-gray-100 hover:text-gray-900'
                                    }`}
                                >
                                    {tab.label}
                                </button>
                            ))}
                        </div>

                        {/* Search */}
                        <div className="relative group flex justify-end">
                            <input
                                type="text"
                                placeholder={isSearchExpanded || searchTerm ? "Siparişlerde ara..." : ""}
                                value={searchTerm}
                                onFocus={() => setIsSearchExpanded(true)}
                                onBlur={() => setIsSearchExpanded(false)}
                                onChange={(e) => setSearchTerm(e.target.value)}
                                className={`transition-all duration-300 ease-in-out border border-gray-200 text-sm focus:outline-none focus:ring-2 focus:ring-brand-green focus:border-transparent bg-gray-50 focus:bg-white overflow-hidden py-2 ${
                                    isSearchExpanded || searchTerm 
                                    ? 'w-[180px] sm:w-48 pl-10 pr-4 rounded-lg' 
                                    : 'w-10 sm:w-10 pl-10 pr-0 rounded-full cursor-pointer hover:bg-gray-100'
                                }`}
                            />
                            <svg 
                                className={`w-5 h-5 absolute left-2.5 top-1/2 transform -translate-y-1/2 pointer-events-none transition-colors duration-300 ${
                                    isSearchExpanded || searchTerm ? 'text-brand-green' : 'text-gray-400'
                                }`} 
                                fill="none" stroke="currentColor" viewBox="0 0 24 24"
                            >
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                            </svg>
                        </div>
                    </div>
                </div>

                {/* Orders Content */}
                <div className="space-y-4">
                    {ordersContent}
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-brand-header relative">
            
            
                <div className="absolute inset-0 bg-gradient-to-tl from-transparent via-brand-green-light/3 to-transparent"></div>
            
            <div className="relative max-w-7xl mx-auto px-3 sm:px-4 py-6 sm:py-12">
                {/* Ultra Premium Hero Header */}
                <div className="text-center mb-8 sm:mb-16 relative">
                    {/* Advanced Floating Elements */}
                    <div className="absolute -top-12 left-1/4 w-6 h-6 bg-gradient-to-r from-brand-green to-emerald-400 rounded-full animate-bounce opacity-60" style={{ animationDelay: '0s' }}></div>
                    <div className="absolute -top-8 right-1/3 w-4 h-4 bg-gradient-to-r from-blue-400 to-cyan-400 rounded-full animate-bounce opacity-50" style={{ animationDelay: '1s' }}></div>
                    <div className="absolute -top-6 left-1/3 w-3 h-3 bg-gradient-to-r from-purple-400 to-pink-400 rounded-full animate-bounce opacity-40" style={{ animationDelay: '2s' }}></div>
                    
                    {/* Premium Main Icon with Multiple Layers */}
                    <div className="relative inline-flex items-center justify-center mb-6 sm:mb-8">
                        {/* Outer Glow */}
                        <div className="absolute inset-0 bg-gradient-to-r from-brand-green via-blue-500 to-purple-500 rounded-full blur-2xl opacity-30 animate-pulse scale-150"></div>
                        {/* Middle Glow */}
                        <div className="absolute inset-0 bg-gradient-to-r from-emerald-400 to-cyan-400 rounded-full blur-xl opacity-40 animate-pulse scale-125" style={{ animationDelay: '1s' }}></div>
                        {/* Inner Container */}
                        <div className="relative w-20 h-20 sm:w-28 sm:h-28 bg-gradient-to-br from-slate-800 via-slate-700 to-slate-800 rounded-full flex items-center justify-center shadow-2xl transform hover:scale-110 transition-all duration-700 border border-white/20">
                            {/* Glass Effect */}
                            <div className="absolute inset-0 bg-gradient-to-br from-white/10 via-transparent to-transparent rounded-full"></div>
                            {/* Icon Background */}
                            <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-brand-green via-emerald-500 to-brand-green rounded-full flex items-center justify-center relative z-10 shadow-xl">
                                <ShoppingBagIcon className="w-8 h-8 sm:w-12 sm:h-12 text-white drop-shadow-lg" />
                                {/* Icon Shine Effect */}
                                <div className="absolute inset-0 bg-gradient-to-tr from-white/30 via-transparent to-transparent rounded-full"></div>
                            </div>
                        </div>
                    </div>
                    
                    {/* Modern Title */}
                    <div className="relative mb-6">
                        <h1 className="text-4xl sm:text-6xl md:text-7xl font-black text-transparent bg-clip-text bg-gradient-to-r from-gray-800 via-brand-green to-brand-green-light mb-4 tracking-tight leading-tight drop-shadow-lg">
                            Siparişlerim
                        </h1>
                        {/* Title Glow Effect */}
                        <div className="absolute inset-0 text-4xl sm:text-6xl md:text-7xl font-black text-brand-green/10 blur-sm">
                            Siparişlerim
                        </div>
                        {/* Animated Underline */}
                        <div className="absolute -bottom-2 left-1/2 transform -translate-x-1/2 w-32 h-1 bg-gradient-to-r from-transparent via-brand-green to-transparent rounded-full animate-pulse"></div>
                    </div>
                    
                    <p className="text-lg sm:text-xl text-gray-700 max-w-3xl mx-auto leading-relaxed mb-8 sm:mb-12 px-4 font-medium">
                        Siparişlerinizi kolayca takip edin ve yönetin
                    </p>
                    
                    {/* Premium Stats Cards - Tema Uyumlu */}
                    <div className="grid grid-cols-3 gap-3 sm:gap-6 md:gap-8 max-w-4xl mx-auto mt-8 sm:mt-12">
                        <div className="group relative bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-4 py-4 sm:px-6 sm:py-6 shadow-xl border border-white/50 text-center hover:bg-white transition-all duration-500 hover:scale-105 hover:-translate-y-1">
                            <div className="absolute inset-0 bg-gradient-to-br from-brand-green/20 to-brand-green-light/20 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            <div className="relative z-10">
                                <div className="text-2xl sm:text-3xl md:text-4xl font-black text-brand-green mb-2">{orders.length}</div>
                                <div className="text-xs sm:text-sm text-gray-600 font-medium">Toplam Sipariş</div>
                            </div>
                        </div>
                        <div className="group relative bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-4 py-4 sm:px-6 sm:py-6 shadow-xl border border-white/50 text-center hover:bg-white transition-all duration-500 hover:scale-105 hover:-translate-y-1">
                            <div className="absolute inset-0 bg-gradient-to-br from-brand-green/20 to-emerald-400/20 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            <div className="relative z-10">
                                <div className="text-2xl sm:text-3xl md:text-4xl font-black text-brand-green mb-2">{orders.filter(o => o.status === 'delivered').length}</div>
                                <div className="text-xs sm:text-sm text-gray-600 font-medium">Teslim Edildi</div>
                            </div>
                        </div>
                        <div className="group relative bg-white/90 backdrop-blur-sm rounded-2xl sm:rounded-3xl px-4 py-4 sm:px-6 sm:py-6 shadow-xl border border-white/50 text-center hover:bg-white transition-all duration-500 hover:scale-105 hover:-translate-y-1">
                            <div className="absolute inset-0 bg-gradient-to-br from-orange-400/20 to-yellow-400/20 rounded-2xl sm:rounded-3xl opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                            <div className="relative z-10">
                                <div className="text-2xl sm:text-3xl md:text-4xl font-black text-orange-600 mb-2">{orders.filter(o => o.status === 'pending').length}</div>
                                <div className="text-xs sm:text-sm text-gray-600 font-medium">Hazırlanıyor</div>
                            </div>
                        </div>
                    </div>
                </div>

                <div className="grid grid-cols-1 lg:grid-cols-4 gap-6 lg:gap-8">
                    <aside className="hidden lg:block lg:col-span-1">
                        <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 overflow-hidden sticky top-6 group hover:bg-white transition-all duration-500">
                            {/* Premium Background Effects */}
                            <div className="absolute inset-0 bg-gradient-to-br from-brand-green/5 via-brand-green-light/3 to-emerald-400/5"></div>
                            
                            {/* Animated Floating Orbs */}
                            
                            
                            
                            
                            <div className="relative p-8">
                                {/* Premium Header */}
                                <div className="text-center mb-8">
                                    <div className="relative inline-flex items-center justify-center mb-4">
                                        {/* Icon Glow Effect */}
                                        <div className="absolute inset-0 w-16 h-16 bg-gradient-to-r from-brand-green to-blue-500 rounded-2xl blur-lg opacity-50 animate-pulse"></div>
                                        
                                        <div className="relative w-14 h-14 bg-gradient-to-br from-brand-green via-emerald-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-xl">
                                            <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-2xl"></div>
                                            <svg className="w-7 h-7 text-white drop-shadow-lg" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 4a1 1 0 011-1h16a1 1 0 011 1v2.586a1 1 0 01-.293.707l-6.414 6.414a1 1 0 00-.293.707V17l-4 4v-6.586a1 1 0 00-.293-.707L3.293 7.207A1 1 0 013 6.5V4z" />
                                            </svg>
                                        </div>
                                    </div>
                                    <h3 className="text-xl font-black text-gray-800 mb-2">Akıllı Filtreler</h3>
                                    <p className="text-sm text-gray-600 font-medium">Siparişlerinizi kategorilere ayırın</p>
                                </div>

                                {/* Ultra Premium Filter Navigation */}
                                <nav className="space-y-4">
                                    {[
                                        { 
                                            id: 'all', 
                                            label: 'Tüm Siparişler', 
                                            count: orders.length, 
                                            icon: ClipboardDocumentIcon,
                                            color: 'from-brand-green via-emerald-500 to-brand-green-light',
                                            glowColor: 'brand-green',
                                            emoji: '📋'
                                        },
                                        { 
                                            id: 'pending', 
                                            label: 'Hazırlanıyor', 
                                            count: orders.filter(o => o.status === 'pending').length, 
                                            icon: ClockIcon,
                                            color: 'from-yellow-500 via-orange-500 to-yellow-600',
                                            glowColor: 'yellow-500',
                                            emoji: '⏳'
                                        },
                                        { 
                                            id: 'shipped', 
                                            label: 'Kargolandı', 
                                            count: orders.filter(o => o.status === 'shipped').length, 
                                            icon: TruckIcon,
                                            color: 'from-blue-500 via-cyan-500 to-blue-600',
                                            glowColor: 'blue-500',
                                            emoji: '🚛'
                                        },
                                        { 
                                            id: 'delivered', 
                                            label: 'Teslim Edildi', 
                                            count: orders.filter(o => o.status === 'delivered').length, 
                                            icon: CheckBadgeIcon,
                                            color: 'from-green-500 via-emerald-500 to-green-600',
                                            glowColor: 'green-500',
                                            emoji: '✅'
                                        },
                                        { 
                                            id: 'cancelled', 
                                            label: 'İptal Edildi', 
                                            count: orders.filter(o => o.status === 'cancelled').length, 
                                            icon: XCircleIcon,
                                            color: 'from-red-500 via-pink-500 to-red-600',
                                            glowColor: 'red-500',
                                            emoji: '❌'
                                        }
                                    ].map((filter, index) => (
                                        <div
                                            key={filter.id}
                                            onClick={() => handleFilterClick(filter.id)}
                                            className={`group relative cursor-pointer rounded-2xl overflow-hidden transition-all duration-500 transform hover:scale-105 hover:-translate-y-1 ${
                                                activeFilter === filter.id 
                                                    ? 'ring-2 ring-brand-green shadow-xl bg-white' 
                                                    : 'hover:bg-white/50'
                                            }`}
                                            style={{ animationDelay: `${index * 0.1}s` }}
                                        >
                                            {/* Premium Background Effects */}
                                            <div className="absolute inset-0 bg-gradient-to-br from-white/5 via-transparent to-white/5"></div>
                                            
                                            {/* Active State Glow */}
                                            {activeFilter === filter.id && (
                                                <div className={`absolute inset-0 bg-gradient-to-r ${filter.color} opacity-20 blur-xl`}></div>
                                            )}
                                            
                                            {/* Hover Glow Effect */}
                                            <div className={`absolute inset-0 bg-gradient-to-r ${filter.color} opacity-0 group-hover:opacity-10 blur-xl transition-opacity duration-500`}></div>
                                            
                                            {/* Content */}
                                            <div className="relative p-5 backdrop-blur-sm">
                                                <div className="flex items-center justify-between mb-3">
                                                    <div className="flex items-center space-x-4">
                                                        {/* Premium Icon with Multiple Effects */}
                                                        <div className="relative">
                                                            {/* Icon Glow */}
                                                            <div className={`absolute inset-0 w-12 h-12 bg-gradient-to-r ${filter.color} rounded-xl blur-lg opacity-50 group-hover:opacity-80 transition-opacity duration-500`}></div>
                                                            
                                                            {/* Icon Container */}
                                                            <div className={`relative w-12 h-12 bg-gradient-to-br ${filter.color} rounded-xl flex items-center justify-center shadow-xl transform group-hover:scale-110 group-hover:rotate-3 transition-all duration-500`}>
                                                                <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent rounded-xl"></div>
                                                                <filter.icon className="w-6 h-6 text-white drop-shadow-lg relative z-10" />
                                                            </div>
                                                            
                                                            {/* Emoji Badge */}
                                                            <div className="absolute -top-1 -right-1 w-6 h-6 bg-white/20 backdrop-blur-sm rounded-full flex items-center justify-center text-xs">
                                                                {filter.emoji}
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Label */}
                                                        <div>
                                                            <span className="font-bold text-gray-800 text-sm group-hover:text-gray-900 transition-colors duration-300">
                                                                {filter.label}
                                                            </span>
                                                            <div className="text-xs text-gray-600 group-hover:text-gray-700 transition-colors duration-300">
                                                                {filter.count === 0 ? 'Sipariş yok' : `${filter.count} sipariş`}
                                                            </div>
                                                        </div>
                                                    </div>
                                                    
                                                    {/* Premium Count Badge */}
                                                    <div className={`relative px-4 py-2 rounded-xl text-sm font-black shadow-xl bg-gradient-to-r ${filter.color} text-white transform group-hover:scale-110 transition-all duration-500`}>
                                                        <div className="absolute inset-0 bg-gradient-to-br from-white/20 to-transparent rounded-xl"></div>
                                                        <span className="relative z-10">{filter.count}</span>
                                                    </div>
                                                </div>
                                                
                                                {/* Animated Progress Bar */}
                                                <div className="h-2 bg-white/10 rounded-full overflow-hidden backdrop-blur-sm">
                                                    <div 
                                                        className={`h-full bg-gradient-to-r ${filter.color} rounded-full transform origin-left transition-all duration-1000 group-hover:shadow-lg`}
                                                        style={{ 
                                                            width: orders.length > 0 ? `${(filter.count / orders.length) * 100}%` : '0%',
                                                            boxShadow: activeFilter === filter.id ? `0 0 20px ${filter.glowColor}` : 'none'
                                                        }}
                                                    ></div>
                                                </div>
                                            </div>
                                        </div>
                                    ))}
                                </nav>
                                
                                {/* Premium Bottom Stats */}
                                <div className="mt-8 pt-6 border-t border-gray-200">
                                    <div className="text-center relative">
                                        {/* Background Glow */}
                                        <div className="absolute inset-0 bg-gradient-to-r from-brand-green/20 to-brand-green-light/20 rounded-2xl blur-xl opacity-50"></div>
                                        
                                        <div className="relative bg-white/80 backdrop-blur-sm rounded-2xl p-6 border border-white/50 shadow-lg">
                                            <div className="text-3xl font-black text-brand-green mb-2">
                                                ₺{orders.reduce((total, order) => total + parseFloat(order.total_price), 0).toFixed(2)}
                                            </div>
                                            <div className="text-sm text-gray-600 font-medium">💰 Toplam Alışveriş Tutarı</div>
                                            
                                            {/* Sparkle Effects */}
                                            <div className="absolute top-2 right-2 w-2 h-2 bg-brand-green rounded-full animate-ping"></div>
                                            <div className="absolute bottom-2 left-2 w-1 h-1 bg-brand-green-light rounded-full animate-ping" style={{ animationDelay: '1s' }}></div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </aside>

                    <main className="col-span-1 lg:col-span-3">
                        <div className="relative bg-white/90 backdrop-blur-sm rounded-3xl shadow-xl border border-white/50 overflow-hidden min-h-[600px] group hover:bg-white transition-all duration-500">
                            {/* Premium Animated Background */}
                            
                            
                            {/* Advanced Floating Elements */}
                            
                            
                            
                            
                            
                            {/* Mesh Gradient Overlay */}
                            <div className="absolute inset-0 bg-gradient-to-br from-transparent via-white/5 to-transparent"></div>
                            
                            <div className="relative p-6 sm:p-8">
                                {/* Ultra Premium Header */}
                                <div className="mb-6 sm:mb-8">
                                    <div className="flex flex-col space-y-6 mb-6 sm:mb-8">
                                        <div className="flex items-center space-x-4">
                                            {/* Premium Icon with Advanced Effects */}
                                            <div className="relative">
                                                {/* Icon Glow */}
                                                
                                                
                                                {/* Icon Container */}
                                                <div className="relative w-14 h-14 sm:w-16 sm:h-16 bg-gradient-to-br from-brand-green via-emerald-500 to-blue-500 rounded-2xl flex items-center justify-center shadow-2xl transform hover:scale-110 hover:rotate-3 transition-all duration-500 group">
                                                    <div className="absolute inset-0 bg-gradient-to-br from-white/30 to-transparent rounded-2xl"></div>
                                                    <ShoppingBagIcon className="w-7 h-7 sm:w-8 sm:h-8 text-white drop-shadow-lg relative z-10 group-hover:scale-110 transition-transform duration-500" />
                                                    
                                                    {/* Orbiting Particles */}
                                                    
                                                        <div className="absolute bottom-0 right-0 w-1 h-1 bg-blue-300 rounded-full"></div>
                                                        <div className="absolute top-1/2 left-0 transform -translate-y-1/2 w-1 h-1 bg-purple-300 rounded-full"></div>
                                                </div>
                                            </div>
                                            
                                            <div>
                                                <h2 className="text-xl sm:text-3xl font-black text-gray-800 mb-1">Sipariş Listesi</h2>
                                                <p className="text-sm sm:text-base text-gray-600 font-medium">Tüm siparişlerinizi detaylı şekilde görüntüleyin</p>
                                            </div>
                                        </div>
                                        
                                        {/* Ultra Premium Arama ve Filtre */}
                                        <div className="space-y-4">
                                            {/* Premium Mobil Filter Dropdown */}
                                            <div className="lg:hidden relative">
                                                <div className="absolute inset-0 bg-gradient-to-r from-brand-green/20 to-blue-500/20 rounded-2xl blur-lg opacity-50"></div>
                                                <select
                                                    value={activeFilter}
                                                    onChange={(e) => handleFilterClick(e.target.value)}
                                                    className="relative w-full px-6 py-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl focus:ring-2 focus:ring-brand-green focus:border-brand-green text-white text-sm font-medium shadow-xl transition-all duration-300 hover:bg-white/20"
                                                >
                                                    <option value="all" className="bg-slate-800 text-white">📋 Tüm Siparişler ({orders.length})</option>
                                                    <option value="pending" className="bg-slate-800 text-white">⏳ Hazırlanıyor ({orders.filter(o => o.status === 'pending').length})</option>
                                                    <option value="shipped" className="bg-slate-800 text-white">🚛 Kargolandı ({orders.filter(o => o.status === 'shipped').length})</option>
                                                    <option value="delivered" className="bg-slate-800 text-white">✅ Teslim Edildi ({orders.filter(o => o.status === 'delivered').length})</option>
                                                    <option value="cancelled" className="bg-slate-800 text-white">❌ İptal Edildi ({orders.filter(o => o.status === 'cancelled').length})</option>
                                                </select>
                                            </div>

                                            <div className="flex gap-3">
                                                {/* Premium Search Input */}
                                                <div className="relative flex-1 group">
                                                    {/* Search Glow Effect */}
                                                    
                                                    
                                                    <div className="relative">
                                                        <input
                                                            type="text"
                                                            placeholder="🔍 Siparişlerinizi arayın..."
                                                            value={searchTerm}
                                                            onChange={(e) => setSearchTerm(e.target.value)}
                                                            className="w-full pl-12 pr-4 py-4 bg-white/80 backdrop-blur-sm border border-gray-300 rounded-2xl focus:ring-2 focus:ring-brand-green focus:border-brand-green text-gray-800 placeholder-gray-500 text-sm font-medium shadow-lg transition-all duration-300 hover:bg-white focus:bg-white"
                                                        />
                                                        
                                                        {/* Premium Search Icon */}
                                                        <div className="absolute left-4 top-1/2 transform -translate-y-1/2">
                                                            <div className="w-6 h-6 bg-gradient-to-r from-brand-green to-blue-500 rounded-lg flex items-center justify-center">
                                                                <svg className="w-4 h-4 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                        
                                                        {/* Search Animation */}
                                                        {searchTerm && (
                                                            <div className="absolute right-4 top-1/2 transform -translate-y-1/2">
                                                                <div className="w-2 h-2 bg-brand-green rounded-full animate-ping"></div>
                                                            </div>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Premium Clear Button */}
                                                {searchTerm && (
                                                    <button
                                                        onClick={() => setSearchTerm('')}
                                                        className="relative px-6 py-4 bg-white/10 backdrop-blur-xl border border-white/20 rounded-2xl text-white font-medium shadow-xl transition-all duration-300 hover:bg-white/20 hover:scale-105 group flex-shrink-0"
                                                    >
                                                        <div className="absolute inset-0 bg-gradient-to-r from-red-500/20 to-pink-500/20 rounded-2xl blur-lg opacity-0 group-hover:opacity-100 transition-opacity duration-300"></div>
                                                        <span className="relative z-10">✨ Temizle</span>
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    
                                    {/* Premium Sonuç Sayısı */}
                                    <div className="relative mb-6">
                                        <div className="bg-white/10 backdrop-blur-xl rounded-2xl p-4 border border-white/20 shadow-xl">
                                            <div className="flex items-center justify-between">
                                                <div className="flex items-center space-x-3">
                                                    <div className="w-8 h-8 bg-gradient-to-r from-brand-green to-blue-500 rounded-lg flex items-center justify-center">
                                                        <span className="text-white font-bold text-sm">{filteredOrders.length}</span>
                                                    </div>
                                                    <div>
                                                        <p className="text-white font-medium text-sm">
                                                            {filteredOrders.length} sipariş bulundu
                                                            {searchTerm && ` (${orders.length} siparişten)`}
                                                        </p>
                                                        {activeFilter !== 'all' && (
                                                            <p className="text-gray-300 text-xs">📊 {activeFilter} durumu filtrelendi</p>
                                                        )}
                                                    </div>
                                                </div>
                                                
                                                {/* Animated Progress Indicator */}
                                                <div className="flex space-x-1">
                                                    <div className="w-2 h-2 bg-brand-green rounded-full animate-pulse"></div>
                                                    <div className="w-2 h-2 bg-blue-400 rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
                                                    <div className="w-2 h-2 bg-purple-400 rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                                
                                {/* Orders Content - Mobil Optimized */}
                                <div className="space-y-1 sm:space-y-3">
                                    {ordersContent}
                                </div>
                                
                                {/* Bottom Gradient */}
                                <div className="absolute bottom-0 left-0 right-0 h-16 bg-gradient-to-t from-white/60 to-transparent pointer-events-none"></div>
                            </div>
                        </div>
                    </main>
                </div>
            </div>
            {/* Yorum Yazma Drawer'i */}
            {reviewFormData && (
                <div className="fixed inset-0 z-50 overflow-hidden">
                    {/* Backdrop */}
                    <div 
                        className="absolute inset-0 bg-black/60 backdrop-blur-sm transition-opacity"
                        onClick={() => setReviewFormData(null)}
                    />
                    
                    {/* Drawer Panel */}
                    <div className="fixed inset-y-0 right-0 max-w-full flex">
                        <div className="w-screen max-w-2xl transform transition-transform ease-in-out duration-300">
                            <div className="h-full flex flex-col bg-white shadow-2xl">
                                {/* Drawer Header */}
                                <div className="px-6 py-6 bg-gradient-to-b from-green-50 to-white border-b border-green-100">
                                    <div className="flex items-center justify-between">
                                        <div>
                                            <h3 className="text-2xl font-bold text-gray-900">
                                                ✍️ Değerlendirme Yaz
                                            </h3>
                                            <p className="text-sm text-gray-600 mt-1">
                                                {reviewFormData.productName}
                                            </p>
                                        </div>
                                        <button
                                            onClick={() => setReviewFormData(null)}
                                            className="w-10 h-10 rounded-xl bg-gray-100 hover:bg-gray-200 transition-colors flex items-center justify-center"
                                        >
                                            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                                            </svg>
                                        </button>
                                    </div>
                                </div>
                                
                                {/* Drawer Body - Scrollable */}
                                <div className="flex-1 overflow-y-auto px-6 py-6">
                                    <ReviewForm 
                                        productId={reviewFormData.productId}
                                        orderId={reviewFormData.orderId}
                                        productName={reviewFormData.productName}
                                        onSubmit={() => {
                                            setReviewFormData(null);
                                            toast.success('🎉 Yorumunuz başarıyla eklendi! Admin onayından sonra yayınlanacaktır.');
                                        }}
                                        onCancel={() => setReviewFormData(null)}
                                    />
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

export default OrdersPage;
